import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { assignCourse, resetCourseProgress, getOrCreateTrainingLinkToken } from '../../lib/training';
import { notifyTrainingAssigned } from '../../lib/notifications';

const STATUS_LABEL = { assigned: 'Assigned', in_progress: 'In progress', completed: 'Completed' };

// Assigns by COURSE ("Learning Path"), not individual module — matches how
// real LMS tools bundle a curriculum into one assignable/certifiable unit.
// Assigning a course creates every one of its module records in one action, so
// the caregiver gets one link/one email covering the whole thing, not one per
// section (see assignCourse in lib/training.js).
//
// Already-assigned courses are shown too (not hidden), so a broken/incomplete
// assignment can be re-run to backfill missing sections, or a completed course
// can be explicitly reset for redo — reassignment isn't blocked by a prior
// assignment existing.
export default function AssignTrainingModal({ staff, courses, existingCourseRecords, onClose, onAssigned }) {
  const { profile } = useAuth();
  const existingByCourse = new Map((existingCourseRecords || []).map(r => [r.course_id, r]));
  const [selected, setSelected] = useState(new Set());
  const [resetCourses, setResetCourses] = useState(new Set());
  const [dueDate, setDueDate] = useState('');
  const [reasonNote, setReasonNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [email, setEmail] = useState(staff.email || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleReset = (id) => setResetCourses(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleAssign = async () => {
    if (selected.size === 0) { setError('Select at least one course.'); return; }
    setSaving(true);
    setError('');

    const allCreated = [];
    for (const courseId of selected) {
      const existing = existingByCourse.get(courseId);
      const isCompleted = existing?.status === 'completed';
      // A completed course is only touched if the coordinator explicitly
      // checked "reset progress" — otherwise skip the DB call entirely so a
      // plain resend-the-link selection can never silently flip a completed
      // course's status back to 'assigned' while leaving its completion date/
      // signature/certificate in place (an inconsistent half-reset state).
      if (isCompleted && !resetCourses.has(courseId)) continue;
      if (resetCourses.has(courseId)) {
        const { error: resetErr } = await resetCourseProgress({ staffId: staff.id, courseId });
        if (resetErr) { setError(resetErr.message); setSaving(false); return; }
      }
      const { data, error: err } = await assignCourse({ staffId: staff.id, courseId, dueDate, reasonNote, assignedBy: profile?.id });
      if (err) { setError(err.message); setSaving(false); return; }
      allCreated.push(...(data?.created || []));
    }

    // Notify on selection, not on allCreated.length — a reassign/resend for
    // someone whose section rows already exist (a backfilled or previously-
    // broken assignment, or a plain resend) creates zero *new* rows but still
    // needs the link (re)sent.
    if (notify && selected.size > 0) {
      const sendTo = email.trim();
      if (sendTo) {
        // One link per person, not per course/module — reuse an existing unfinished
        // training token if this staff member already has one, so repeat assignments
        // don't spam a fresh email/link every time.
        const { token, error: tokenErr } = await getOrCreateTrainingLinkToken(staff.id);
        if (tokenErr) { setError('Assigned, but the notification link could not be created: ' + tokenErr.message); setSaving(false); return; }
        const courseNames = [...selected].map(id => courses.find(c => c.id === id)?.title).filter(Boolean);
        await notifyTrainingAssigned(sendTo, staff.full_name, courseNames, `${window.location.origin}/training-link/${token}`);
      }
    }
    setSaving(false);
    onAssigned();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: 'var(--bg)', borderRadius: 12, width: '100%', maxWidth: 460, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Assign training</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>{staff.last_name ? `${staff.last_name}, ${staff.first_name}` : staff.full_name}</div>

        {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Course *</div>
            {(courses || []).length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text3)', padding: '8px 0' }}>No courses configured.</div>
            ) : (
              <div style={{ border: '0.5px solid var(--border)', borderRadius: 6 }}>
                {(courses || []).map(c => {
                  const existing = existingByCourse.get(c.id);
                  const isCompleted = existing?.status === 'completed';
                  const isSelected = selected.has(c.id);
                  return (
                    <div key={c.id} style={{ borderBottom: '0.5px solid var(--border)' }}>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px', fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox" checked={isSelected} onChange={() => toggle(c.id)} style={{ marginTop: 2 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ fontWeight: 500 }}>{c.title}</div>
                            {existing && (
                              <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 10, background: isCompleted ? 'var(--success-bg)' : 'var(--warn-bg)', color: isCompleted ? 'var(--success)' : 'var(--warn)' }}>
                                {STATUS_LABEL[existing.status] || existing.status}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                            {c.training_modules?.length || 0} sections · renews {c.renewal_cadence}
                            {existing ? ' · reassign to backfill or resend the link' : ''}
                          </div>
                        </div>
                      </label>
                      {isSelected && isCompleted && (
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '0 10px 10px 32px', fontSize: 12, color: 'var(--danger)', cursor: 'pointer' }}>
                          <input type="checkbox" checked={resetCourses.has(c.id)} onChange={() => toggleReset(c.id)} style={{ marginTop: 2 }} />
                          Reset progress — clears the prior completion, signature, and certificate for this course
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Due date</div>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Reason (optional)</div>
            <textarea rows={2} value={reasonNote} onChange={e => setReasonNote(e.target.value)} placeholder="e.g. corrective training after incident review" style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text2)', cursor: 'pointer', marginBottom: notify ? 6 : 0 }}>
              <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} />
              Email a no-login training link now (one link covers everything assigned)
            </label>
            {notify && (
              <input type="email" placeholder="Recipient email" value={email} onChange={e => setEmail(e.target.value)}
                style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }} />
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px 0', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleAssign} disabled={saving || selected.size === 0 || (notify && !email.trim())} style={{ flex: 2, padding: '10px 0', background: saving ? '#aaa' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: saving ? 'default' : 'pointer', fontFamily: 'inherit' }}>
            {saving ? 'Assigning…' : `Assign ${selected.size || ''} course${selected.size === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
